// Shared react-markdown `components` override for rendering AI reports —
// used by the live AI Reports page (AIReportPanel.jsx) AND wherever a saved
// or shared report's markdown is displayed later (TeamPlayerPanels.jsx), so
// a saved/shared report looks identical to the original/PDF version instead
// of showing raw ```chart fenced blocks or unstyled tables.
import AIReportChart, {
    chartFromPreNode,
    chartSpecFromTableNode,
} from "./AIReportChart";

export const reportMarkdownComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="text-base font-bold mt-3 mb-1" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-sm font-bold mt-3 mb-1" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1" {...props} />
  ),
  p: ({ node, ...props }) => <p className="mb-2" {...props} />,
  ul: ({ node, ...props }) => (
    <ul className="list-disc ml-4 mb-2 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal ml-4 mb-2 space-y-0.5" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  table: ({ node, ...props }) => {
    // Prefer a chart: if the table is numeric (a label column plus numeric
    // columns), render it as a bar chart instead of a table for better visuals.
    const spec = chartSpecFromTableNode(node);
    if (spec) {
      return <AIReportChart spec={spec} />;
    }
    return (
      <table className="table-auto border-collapse my-2 text-[11px]" {...props} />
    );
  },
  th: ({ node, ...props }) => (
    <th className="border px-2 py-1 text-left" {...props} />
  ),
  td: ({ node, ...props }) => <td className="border px-2 py-1" {...props} />,
  pre: ({ node, children, ...props }) => {
    // A fenced code block renders as <pre><code>. If it is a chart spec (tagged
    // ```chart or chart JSON), render the chart directly instead of the code so
    // it is not wrapped in an invalid <pre><div> nesting.
    const chart = chartFromPreNode(node);
    if (chart) {
      return <AIReportChart spec={chart.text} />;
    }
    return (
      <pre className="my-2 overflow-x-auto rounded bg-base-200 p-2 text-[10px]" {...props}>
        {children}
      </pre>
    );
  },
};
